import React, { Component } from "react";
import cn from "classnames";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { FormattedMessage } from "react-intl";

import { nameMinLength, nameMaxLength, deparseDescriptions, getLocale } from "../../../../common/parameters";
import getDescription from "../../../../common/helpers/getDescription";
import getName, { getNameLang } from "../../../../common/helpers/getName";
import deleteUndefined from "../../../../common/helpers/deleteUndefined";

import Icon from "../../../icon";
import { generateAudioPath, host } from "../../../paths";
import player from "../../../player";

import User from "../../../components/User";
import RenamePlan from "../../../components/PlanCard/Dropdown/RenamePlan";
import Modal from "../../../components/Modal";
import { Title, Cancel } from "../../../components/Form";

import Image from "./Image";
import ChangeDescription from "./ChangeDescription";

import {
    StyledMiddleRow,
    BasicInfo,
    PlanTitle,
    TitleText,
    Creator,
    By,
    PlanDescription,
    DescriptionnText,
    Composer,
    ComposerContainer,
    ComposerImg,
    PlayButton,
    TrashButton,
    AudioButtons,
} from "./styles";

import messages from "./messages";
import { IProps, IState } from "./interfaces";
import { formatComposerName } from "../../../../common/helpers/formatComposerName";

const trimDescriptions = (descriptions: {[lang: string]: string}) => {
    let trimedDescriptions: {[lang: string]: string} = {};
    for (const [lang, description] of Object.entries(descriptions)) {
        trimedDescriptions[lang] = description.trim();
    }
    return trimedDescriptions;
};

class MiddleRow extends Component<IProps, IState> {
    state: IState = {
        editPlanDescription: {},
        editPlanTitle: {},
        validationPlanName: {},
        playing: false,
        loadingChangeDescription: false,
        changeDescription: false,
        loadingRenamePlan: false,
        changeTitle: false,
        tooBigSize: false,
        incorrectType: false,
        audioUploadInProgress: false,
        loadingAudio: false,
    };
    audioUploadRef = React.createRef<HTMLInputElement>();
    audioBuffer?: {
        createNode: (volume: any) => AudioBufferSourceNode
    }
    audioInstance?: AudioBufferSourceNode;
    componentWillUnmount() {
        if (this.state.playing) {
            this.play();
        }
    }
    submitChangeDescription = () => {
        const { editPlanDescription } = this.state;
        const { plan } = this.props;
        this.setState({ loadingChangeDescription: true });
        this.props.setPlanDescription(
            { planId: plan._id, description: deleteUndefined(trimDescriptions(editPlanDescription)) },
            success => {
                this.setState({ changeDescription: !success, loadingChangeDescription: false });
            }
        );
    };

    changePlanDescription = (e: React.FormEvent<HTMLTextAreaElement>) => {
        this.setState({
            editPlanDescription: {
                ...this.state.editPlanDescription,
                [e.currentTarget.name]: e.currentTarget.value,
            },
        });
    }

    renamePlan = () => {
        let newName = this.state.editPlanTitle;
        const {
            intl: { locale },
        } = this.props;

        if (newName[getLocale(locale)].length === 0) {
            this.setState({ validationPlanName: { [getLocale(locale)]: "required" } });
        } else if (newName[getLocale(locale)].length < nameMinLength) {
            this.setState({ validationPlanName: { [getLocale(locale)]: "tooShortPlanName" } });
        } else if (nameMaxLength < newName[getLocale(locale)].length) {
            this.setState({ validationPlanName: { [getLocale(locale)]: "tooLongPlanName" } });
        } else {
            const { plan } = this.props;
            this.setState({ loadingRenamePlan: true });
            this.props.setPlanTitle({ planId: plan._id, name: deleteUndefined(newName) }, success => {
                this.setState({ changeTitle: !success, loadingRenamePlan: false });
            });
        }
    };
    renameChange = (e: React.FormEvent<HTMLInputElement>) => {
        this.setState({
            editPlanTitle: {
                ...this.state.editPlanTitle,
                [e.currentTarget.name]: e.currentTarget.value,
            },
            validationPlanName: {
                ...this.state.validationPlanName,
                [e.currentTarget.name]: undefined,
            },
        });
    };

    uploadAudioChange = (e: React.FormEvent<HTMLInputElement>) => {
        const { files } = e.target as HTMLInputElement;
        if (!files || files.length === 0) return;
        if (files[0].size > 1024 * 1024 * 10) {
            this.setState({ tooBigSize: files[0].size });
            return;
        }
        if (
            files[0].type !== "audio/mpeg3" &&
            files[0].type !== "audio/mp3" &&
            files[0].type !== "audio/mpeg"
        ) {
            this.audioUploadRef.current!.value = "";
            return this.setState({ incorrectType: true });
        }
        const { plan, post, addAudio } = this.props;
        const data = new FormData();

        data.append("audio", files[0]);
        data.append("planId", plan._id);
        this.setState({ audioUploadInProgress: true });
        post(
            "plan-audio-upload",
            data,
            (res: AxiosResponse<{ file: string }>) => {
                const { data } = res;
                this.audioUploadRef.current!.value = "";
                this.setState({ audioUploadInProgress: false });
                if (data.file) {
                    addAudio(data.file);
                }
            },
            () => {
                this.setState({ audioUploadInProgress: false });
            }
        );
    };
    removeAudio = () => {
        const { post, plan, removeAudio } = this.props;
        this.setState({ audioUploadInProgress: true });
        post(
            "plan-delete-audio",
            { planId: plan._id },
            () => {
                this.audioBuffer = undefined;
                this.setState({ audioUploadInProgress: false });
                removeAudio();
            },
            () => {
                this.setState({ audioUploadInProgress: false });
            }
        );
    };

    play = () => {
        if (this.state.playing) {
            this.audioInstance!.stop();
            return;
        }
        if (this.audioBuffer) {
            this.startPlaying();
        } else {
            const { plan } = this.props;
            let options: AxiosRequestConfig = { responseType: "arraybuffer" };
            this.setState({ loadingAudio: true });
            axios.get(generateAudioPath(plan.audio!), options).then(async res => {
                const decodeResult = await player.decodeAudio(res.data);
                if (!decodeResult) return;
                const { createNode } = decodeResult;
                this.audioBuffer = { createNode };
                this.setState({ loadingAudio: false });
                this.startPlaying();
            });
        }
    };
    startPlaying() {
        this.audioInstance = this.audioBuffer!.createNode(undefined);
        this.audioInstance.start();
        this.setState({ playing: true });
        this.audioInstance.onended = () => {
            this.setState({ playing: false });
        };
    }

    render() {
        const {
            changeDescription,
            editPlanDescription,
            editPlanTitle,
            changeTitle,
            validationPlanName,
            loadingChangeDescription,
            loadingRenamePlan,
            tooBigSize,
            incorrectType,
            audioUploadInProgress,
            loadingAudio,
            playing,
        } = this.state;
        const {
            plan,
            publicView,
            simulation,
            intl: { locale },
            intl,
            user,
        } = this.props;
        const description = getDescription(plan.description, intl);
        const name = getName(plan.name, intl);
        const lang = getNameLang(plan.name, name);
        return (
            <StyledMiddleRow>
                <Image
                    planId={plan._id}
                    cover={plan.cover}
                    publicView={publicView}
                    plan={plan}
                    simulation={simulation}
                />
                <BasicInfo>
                    <PlanTitle>
                        <TitleText lang={lang}>
                            {!!plan?.meta?.opus?.composer && plan.meta.opus.composer !== "unknown" && (
                                <Composer>
                                    <ComposerContainer>
                                        <ComposerImg
                                            style={{ backgroundImage: `url(${host}composers/${plan.meta.opus.composerImg})` }}
                                        />
                                        <div>{formatComposerName(plan.meta.opus.composerName || "", locale)}</div>
                                    </ComposerContainer>
                                </Composer>
                            )}
                            {name}
                            {!publicView && (
                                <span
                                    className={"EditTitle"}
                                    onClick={() => {
                                        !simulation &&
                                            this.setState({
                                                changeTitle: true,
                                                editPlanTitle: {
                                                    [getLocale(locale)]: getName(plan.name, intl),
                                                    ...plan.name,
                                                },
                                            });
                                    }}
                                >
                                    <Icon i="edit" />
                                </span>
                            )}
                            {changeTitle && (
                                <RenamePlan
                                    close={() => this.setState({ changeTitle: false })}
                                    changeName={this.renameChange}
                                    rename={this.renamePlan}
                                    planName={editPlanTitle}
                                    validationPlanName={validationPlanName}
                                    loading={loadingRenamePlan}
                                />
                            )}
                        </TitleText>
                        {publicView && (
                            <Creator>
                                <div>
                                    <By>
                                        <FormattedMessage {...messages.createdBy} />:{" "}
                                    </By>
                                    <User user={plan.userData!} />
                                </div>
                            </Creator>
                        )}
                    </PlanTitle>
                    <PlanDescription as="div">
                        <p>
                            <AudioButtons>
                                {plan.audio && (
                                    <PlayButton
                                        disabled={loadingAudio}
                                        onClick={this.play}
                                        className={cn("button button--play", { "button--loading": loadingAudio })}
                                    >
                                        {playing ? <Icon i="stop" /> : <Icon i="play" />}{" "}
                                        <FormattedMessage {...messages.play} />
                                    </PlayButton>
                                )}
                                {!plan.audio && !publicView && (
                                    <PlayButton
                                        className={cn("button button--play", {
                                            "button--loading": audioUploadInProgress,
                                        })}
                                        disabled={audioUploadInProgress}
                                        onClick={() => {
                                            if (!audioUploadInProgress) this.audioUploadRef.current!.click();
                                        }}
                                    >
                                        + <FormattedMessage {...messages.addAudio} />
                                    </PlayButton>
                                )}
                                {plan.audio && !publicView && (
                                    <TrashButton
                                        onClick={this.removeAudio}
                                        className={cn("button", { "button--loading": audioUploadInProgress })}
                                        disabled={audioUploadInProgress}
                                    >
                                        <Icon i="trash" />
                                    </TrashButton>
                                )}
                            </AudioButtons>
                            <input
                                type="file"
                                accept="audio/mpeg3"
                                style={{ display: "none" }}
                                ref={this.audioUploadRef}
                                onChange={this.uploadAudioChange}
                            />
                            {tooBigSize && (
                                <Modal close={() => this.setState({ tooBigSize: false })}>
                                    <Title>
                                        <FormattedMessage {...messages.fileUpload} />
                                    </Title>
                                    <p>
                                        <FormattedMessage {...messages.fileTooLarge} />: {Math.ceil(tooBigSize / 1024)}{" "}
                                        KB
                                    </p>
                                    <p>
                                        <FormattedMessage {...messages.fileSizeLimit} values={{ value: "10" }} />
                                    </p>
                                    <Cancel onClick={() => this.setState({ tooBigSize: false })}>
                                        <FormattedMessage {...messages.close} />
                                    </Cancel>
                                </Modal>
                            )}
                            {incorrectType && (
                                <Modal close={() => this.setState({ incorrectType: false })}>
                                    <Title>
                                        <FormattedMessage {...messages.fileUpload} />
                                    </Title>
                                    <p>
                                        <FormattedMessage {...messages.fileIncorrectFormat} />
                                    </p>
                                    <p>
                                        <FormattedMessage {...messages.uploadMp3} />
                                    </p>
                                    <Cancel onClick={() => this.setState({ incorrectType: false })}>
                                        <FormattedMessage {...messages.close} />
                                    </Cancel>
                                </Modal>
                            )}
                            {description ? (
                                <DescriptionnText lang={lang} dangerouslySetInnerHTML={{ __html: description }} />
                            ) : (
                                <FormattedMessage {...messages.noDescription} />
                            )}
                            {!publicView && (
                                <span
                                    className={"EditDescription"}
                                    onClick={() => {
                                        !simulation &&
                                            this.setState({
                                                changeDescription: true,
                                                editPlanDescription: deparseDescriptions({
                                                    [getLocale(locale)]: getDescription(plan.description, intl),
                                                    ...plan.description,
                                                }),
                                            });
                                    }}
                                >
                                    <Icon i="edit" />
                                </span>
                            )}
                        </p>
                        {changeDescription && (
                            <ChangeDescription
                                close={() => this.setState({ changeDescription: false })}
                                submit={this.submitChangeDescription}
                                planDescription={editPlanDescription}
                                changeDescription={this.changePlanDescription}
                                user={user}
                                loading={loadingChangeDescription}
                            />
                        )}
                    </PlanDescription>
                </BasicInfo>
            </StyledMiddleRow>
        );
    }
}

export default MiddleRow;
